import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Input, DatePicker, Upload, Icon, Transfer, Progress } from 'antd';
import classnames from 'classnames';
import moment from 'moment';
import { useLazyQuery } from '@apollo/client';
import qs from 'query-string';

import { imageUrl } from '~helpers/gallery';
import { queryCity, queryBannerDetail, getActiveCities } from '../../../gql';

import { handleBeforeUpload } from './upload-banner';

function AddBannerModal({
  t,
  onClose,
  visible,
  form,
  citySlug,
  locale,
  loading,
  updateLoading,
  createBanner,
  updateHeroBanner,
  bannerId,
}) {
  const URL_REGEXP =
    /(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})?/;
  const { getFieldDecorator, setFields, validateFields, resetFields } = form;
  const { cityId } = qs.parse(window.location.search);
  const [uploadImageUrl, setUploadImageUrl] = useState();
  const [uploadFail, setUploadFail] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [imageParams, setImageParams] = useState();
  const [activeRadio, setActiveRadio] = useState();
  const [currentCountry, setCurrentCountry] = useState();
  const [allCities, setAllCities] = useState([]);
  const [bannerData, setBannerData] = useState({});
  const [targetKeys, setTargetKeys] = useState([cityId]);
  const [endDateIsEmpty, setEndDateIsEmpty] = useState(false);
  const [uploadingStatus, setUploadingStatus] = useState(bannerId ? 'DONE' : 'TODO');

  const handleAfterUpload = (imageData, url, uploadError, status) => {
    if (!uploadError) {
      setUploadImageUrl(url);
      setImageParams(imageData);
    }
    setUploadingStatus(status);
    setUploadFail(uploadError);
  };

  const handleUpload = file => {
    handleBeforeUpload(file, handleAfterUpload);
  };

  const handleSelectStartDate = (_, date) => {
    if (moment(date).isAfter(endDate)) {
      setEndDate(null);
      setFields({
        effectiveEndDate: null,
      });
    }
    setStartDate(moment(date));
  };

  const handleSelectEndDate = (date, dateString) => {
    setEndDate(moment(dateString));
    setEndDateIsEmpty(false);
    setFields({ effectiveEndDate: { value: date, errors: [] } });
  };

  const handleSelectApply = activeKey => {
    setActiveRadio(activeKey);
  };

  const handleOnClose = () => {
    resetFields();
    onClose();
  };

  const handleSelectCityChange = targetKeys => {
    setTargetKeys(targetKeys);
  };

  const handleOnsubmit = () => {
    validateFields((err, values) => {
      if (!uploadImageUrl) {
        setUploadFail(!uploadImageUrl);
        return;
      }

      if (err?.effectiveEndDate?.errors) {
        setEndDateIsEmpty(true);
      }

      if (!err) {
        const { effectiveEndDate, effectiveStartDate, link } = values;
        let params = {
          effectiveEndDate: moment(effectiveEndDate).format('YYYY-MM-DD'),
          effectiveStartDate: moment(effectiveStartDate).format('YYYY-MM-DD'),
          link,
          locale,
          allCountries: activeRadio === 0,
          ...imageParams,
        };

        if (!activeRadio || activeRadio === 2) {
          if (!activeRadio) {
            params = Object.assign(params, {
              cities: { cityId },
              countryId: null,
            });
          } else if (activeRadio === 2) {
            const cities = targetKeys.map(city => ({ cityId: city }));
            params = Object.assign(params, { cities, countryId: null });
          }
        }

        if (activeRadio === 1) {
          delete params.cities;
          params = Object.assign(params, { countryId: currentCountry.id });
        }

        if (activeRadio === 0) {
          delete params.cities;
        }

        if (bannerId) {
          delete params.locale;
          updateHeroBanner({ variables: { input: { id: bannerId, ...params } } });
        } else {
          createBanner({ variables: { input: params } });
        }
      }
    });
  };

  const [queryActiveCities] = useLazyQuery(getActiveCities, {
    fetchPolicy: 'no-cache',
    onCompleted: cities => {
      const citiesResult = cities.getActiveCities.edges?.filter(item => item?.node?.id !== cityId);
      setAllCities(citiesResult);
    },
  });

  const [getAllCities] = useLazyQuery(queryCity, {
    fetchPolicy: 'no-cache',
    variables: {
      slug: citySlug,
    },
    onCompleted: data => {
      const { country } = data.city;
      setCurrentCountry(country);
      if (country.slug) {
        queryActiveCities({ variables: { countrySlugs: [country.slug] } });
      }
    },
  });

  const [getBanner] = useLazyQuery(queryBannerDetail, {
    fetchPolicy: 'no-cache',
    variables: {
      id: bannerId,
    },
    onCompleted: bannerData => {
      const result = bannerData.getBanner?.banner;
      setBannerData(result);
      if (result.allCountries) {
        setActiveRadio(0);
      } else if (result.country?.id) {
        setActiveRadio(1);
      } else if (result.cities?.length > 0 && result.cities?.some(city => city.id !== cityId)) {
        setActiveRadio(2);
        const applyCities = [];
        result.cities.forEach(city => applyCities.push(city.id));
        setTargetKeys(applyCities);
      }

      setUploadImageUrl(
        imageUrl(
          {
            source: `${result.filename}-${result.imageHash}.${result.extension}`,
          },
          { width: 373, height: 160 },
        ),
      );
    },
  });

  const handleDeleteImg = () => {
    setUploadingStatus('TODO');
    setUploadImageUrl(null);
  };

  useEffect(() => {
    getAllCities();
    if (bannerId) {
      getBanner();
    }
  }, []);

  return (
    <Modal
      visible={visible}
      onCancel={handleOnClose}
      width={812}
      wrapClassName="add-banner-modal"
      footer={null}
      centered
      maskClosable={false}
      destroyOnClose={true}
    >
      <div className="add-banner-modal__content">
        <h3 className="add-banner-modal__title">
          {bannerId ? 'Edit new banner' : 'Add new banner'}
        </h3>
        <Form>
          <Form.Item label="Image desktop (373x160)" required>
            {getFieldDecorator('imageHash', {
              valuePropName: 'fileList',
              getValueFromEvent: e => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e && e.fileList;
              },
            })(
              <>
                <If condition={uploadImageUrl && uploadingStatus === 'DONE'}>
                  <div className="banner-uploader-wrapper">
                    <img src={uploadImageUrl} alt="avatar" style={{ width: '100%' }} />
                    <div className={classnames('delete-wrapper')}>
                      <Icon onClick={handleDeleteImg} type="delete" className="delete-icon" />
                    </div>
                  </div>
                </If>
                <If condition={!uploadImageUrl}>
                  <Upload
                    className={classnames('banner-uploader', uploadFail && 'upload-error')}
                    beforeUpload={handleUpload}
                    listType="picture"
                    showUploadList={false}
                    accept="image/*"
                  >
                    <div className="upload-icon-wrapper">
                      <If condition={uploadingStatus === 'UPLOADING'}>
                        <p className="upload-progress-word">Uploading</p>
                        <Progress
                          className="upload-progress"
                          strokeColor="#38B2A6"
                          percent={60}
                          showInfo={false}
                          strokeWidth={2}
                        />
                      </If>
                      <If condition={uploadingStatus === 'TODO'}>
                        <Icon type="plus" className="upload-icon" />
                        <p className="upload-word">Upload</p>
                      </If>
                    </div>
                  </Upload>
                </If>
              </>,
            )}
            {uploadFail && (
              <span className="upload-error-msg">
                {t('cms:cms.listing.modal.error_message.can_not_empty')}
              </span>
            )}
          </Form.Item>
          <Form.Item label={t('Link')} required>
            {getFieldDecorator('link', {
              initialValue: bannerData?.link,
              rules: [
                {
                  required: true,
                  message: t('cms:cms.pms_integrations.list.pms_modal.callback_url.required'),
                },
                {
                  validator: (_, value, callback) => {
                    const valid = URL_REGEXP.test(value);
                    if (value && !valid) {
                      return callback('Please input the right URL.');
                    }
                    return callback();
                  },
                },
              ],
            })(<Input placeholder={t('Input the link')} />)}
          </Form.Item>
          <Form.Item label="Effective date" required className="datepicker-wrapper">
            {getFieldDecorator('effectiveStartDate', {
              initialValue: bannerData?.effectiveStartDate
                ? moment(bannerData?.effectiveStartDate)
                : null,
              rules: [
                {
                  required: true,
                  message: t('cms:cms.special_offer.error.form.start_date'),
                },
              ],
            })(
              <DatePicker
                onChange={handleSelectStartDate}
                format="YYYY-MM-DD"
                placeholder="Select start date"
                className="left-datepicker"
              />,
            )}
            {getFieldDecorator('effectiveEndDate', {
              initialValue: bannerData?.effectiveEndDate
                ? moment(bannerData?.effectiveEndDate)
                : null,
              rules: [
                {
                  required: true,
                  message: t('cms:cms.special_offer.error.form.end_date'),
                },
              ],
            })(
              <DatePicker
                onChange={handleSelectEndDate}
                format="YYYY-MM-DD"
                placeholder="Select end date"
                className={classnames(endDateIsEmpty && 'end-date-picker')}
                disabled={!bannerId && !startDate}
                disabledDate={current => {
                  return current && current < moment(startDate).endOf('day');
                }}
              />,
            )}
            {endDateIsEmpty && (
              <span className="end-date-error">
                {t('cms:cms.special_offer.error.form.end_date')}
              </span>
            )}
          </Form.Item>
          <Form.Item label="Apply to">
            <div>
              <div
                className={classnames('radio-apply', activeRadio === 0 && 'active-radio')}
                onClick={() => handleSelectApply(0)}
              >
                {t('cms:cms.dashboard.strategy.hero_banner.apply_to.all_countries')}
              </div>
              <div
                className={classnames('radio-apply', activeRadio === 1 && 'active-radio')}
                onClick={() => handleSelectApply(1)}
              >
                {currentCountry?.name}
              </div>
              <div
                className={classnames('radio-apply', activeRadio === 2 && 'active-radio')}
                onClick={() => handleSelectApply(2)}
              >
                {t('cms:cms.dashboard.strategy.hero_banner.apply_to.some_cities_in_country_name', {
                  country_name: currentCountry?.name,
                })}
              </div>
              <If condition={activeRadio === 2}>
                <div className="apply-transfer">
                  <div className="transfer-header">
                    <div className="header-title">
                      {t('cms:cms.dashboard.strategy.hero_banner.select_cities')}
                    </div>
                    <div className="header-title">
                      {t('cms:cms.dashboard.strategy.hero_banner.apply_to')}
                    </div>
                  </div>
                  <Transfer
                    dataSource={allCities}
                    rowKey={record => record.node.id}
                    targetKeys={targetKeys}
                    onChange={handleSelectCityChange}
                    render={item => item?.node?.name}
                  />
                </div>
              </If>
            </div>
          </Form.Item>
        </Form>
      </div>
      <div className="footer-bottom">
        <Button onClick={handleOnClose} className="cancel-btn">
          {t('cms:cms.tag_modal.header.btn.cancel')}
        </Button>
        <Button
          type="primary"
          onClick={handleOnsubmit}
          loading={loading || updateLoading}
          className="confirm-btn"
        >
          {t('cms:cms.tag_modal.header.btn.confirm')}
        </Button>
      </div>
    </Modal>
  );
}

AddBannerModal.propTypes = {
  t: PropTypes.func,
  onClose: PropTypes.func,
  visible: PropTypes.bool,
};

AddBannerModal.defaultProps = {
  t: () => {},
  onClose: () => {},
  visible: false,
};

export default Form.create({ name: 'add-banner-modal' })(AddBannerModal);
